export type RuntimeRouteTruthSource = "header" | "routing_payload" | "response_model" | "unknown";

export type RuntimeRouteResolutionStatus = "resolved" | "fallback" | "unresolved";

export type RuntimeRouteBillingSource =
  | "platform_api"
  | "byo_subscription"
  | "byo_api"
  | "local_only"
  | "unknown";

export type RuntimeEconomicsCostBasis =
  | "platform_paid"
  | "customer_paid_byo"
  | "customer_paid_subscription"
  | "local_ollama"
  | "unknown";

export interface RuntimeRouteTruthFields {
  entryRequestedModel: string | null;
  selectedCandidateModel: string | null;
  gatewayRequestedModel: string | null;
  gatewayResolvedModel: string | null;
  actualModel: string | null;
  actualProvider: string | null;
  truthSource: RuntimeRouteTruthSource;
  modelTruthResolved: boolean | null;
  routingDiverged: boolean | null;
  resolutionStatus: RuntimeRouteResolutionStatus | null;
  billingSource: RuntimeRouteBillingSource | null;
  billingDecisionReason: string | null;
  fallbackReason: string | null;
  routeMode: string | null;
  policySource: string | null;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  return value as Record<string, unknown>;
}

export function normalizeRuntimeRouteString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function normalizeRuntimeRouteBoolean(value: unknown): boolean | null {
  if (typeof value === "boolean") return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return null;
}

export function normalizeRuntimeRouteTruthSource(value: unknown): RuntimeRouteTruthSource {
  if (value === "header") return "header";
  if (value === "routing_payload") return "routing_payload";
  if (value === "response_model") return "response_model";
  return "unknown";
}

export function normalizeRuntimeRouteResolutionStatus(
  value: unknown,
): RuntimeRouteResolutionStatus | null {
  if (value === "resolved") return "resolved";
  if (value === "fallback") return "fallback";
  if (value === "unresolved") return "unresolved";
  return null;
}

export function normalizeRuntimeRouteBillingSource(
  value: unknown,
): RuntimeRouteBillingSource | null {
  const normalized = normalizeRuntimeRouteString(value)?.toLowerCase();
  if (!normalized) return null;
  switch (normalized) {
    case "platform":
    case "platform_api":
      return "platform_api";
    case "user_subscription":
    case "customer_subscription":
    case "tenant_subscription":
      return "byo_subscription";
    case "byo_subscription":
      return "byo_subscription";
    case "byo":
    case "byo_api":
    case "byo_key":
      return "byo_api";
    case "local":
    case "local_only":
      return "local_only";
    default:
      return "unknown";
  }
}

export function normalizeRuntimeRouteTruthFields(value: unknown): RuntimeRouteTruthFields {
  const record = asRecord(value) ?? {};
  const actualModel = normalizeRuntimeRouteString(record.actualModel);
  return {
    entryRequestedModel: normalizeRuntimeRouteString(record.entryRequestedModel),
    selectedCandidateModel: normalizeRuntimeRouteString(
      record.selectedCandidateModel ?? record.candidateModel,
    ),
    gatewayRequestedModel: normalizeRuntimeRouteString(
      record.gatewayRequestedModel ?? record.requestedModel,
    ),
    gatewayResolvedModel: normalizeRuntimeRouteString(
      record.gatewayResolvedModel ?? record.resolvedModel,
    ),
    actualModel,
    actualProvider: normalizeRuntimeRouteString(record.actualProvider),
    truthSource: normalizeRuntimeRouteTruthSource(
      record.truthSource ?? record.modelTruthSource ?? (actualModel ? "routing_payload" : null),
    ),
    modelTruthResolved: normalizeRuntimeRouteBoolean(record.modelTruthResolved),
    routingDiverged: normalizeRuntimeRouteBoolean(record.routingDiverged ?? record.diverged),
    resolutionStatus: normalizeRuntimeRouteResolutionStatus(record.resolutionStatus),
    billingSource: normalizeRuntimeRouteBillingSource(record.billingSource),
    billingDecisionReason: normalizeRuntimeRouteString(record.billingDecisionReason),
    fallbackReason: normalizeRuntimeRouteString(record.fallbackReason),
    routeMode: normalizeRuntimeRouteString(record.routeMode),
    policySource: normalizeRuntimeRouteString(record.policySource),
  };
}

export function resolveRuntimeModelTruthResolved(routeTruth: RuntimeRouteTruthFields): boolean {
  if (typeof routeTruth.modelTruthResolved === "boolean") {
    return routeTruth.modelTruthResolved;
  }
  return routeTruth.truthSource !== "unknown" && Boolean(routeTruth.actualModel);
}

export function resolveRuntimeRouteResolutionStatus(
  routeTruth: RuntimeRouteTruthFields,
  params?: { backend?: string | null; actualProvider?: string | null },
): RuntimeRouteResolutionStatus {
  if (routeTruth.resolutionStatus) return routeTruth.resolutionStatus;
  if (resolveRuntimeModelTruthResolved(routeTruth) && routeTruth.actualModel) return "resolved";

  const backend = normalizeRuntimeRouteString(params?.backend)?.toLowerCase();
  const provider = normalizeRuntimeRouteString(
    params?.actualProvider ?? routeTruth.actualProvider,
  )?.toLowerCase();
  if (backend === "ollama" || backend === "synthetic" || provider === "ollama") {
    return "fallback";
  }
  return "unresolved";
}

export function resolveLegacyRequestedModel(routeTruth: RuntimeRouteTruthFields): string {
  return (
    routeTruth.gatewayRequestedModel ??
    routeTruth.selectedCandidateModel ??
    routeTruth.entryRequestedModel ??
    routeTruth.gatewayResolvedModel ??
    routeTruth.actualModel ??
    "unknown"
  );
}

export function classifyRuntimeEconomicsCostBasis(params: {
  billingSource?: string | null;
  backend?: string | null;
  actualProvider?: string | null;
}): RuntimeEconomicsCostBasis {
  const billingSource = normalizeRuntimeRouteBillingSource(params.billingSource);
  const backend = normalizeRuntimeRouteString(params.backend)?.toLowerCase();
  const provider = normalizeRuntimeRouteString(params.actualProvider)?.toLowerCase();

  if (backend === "ollama" || provider === "ollama" || billingSource === "local_only") {
    return "local_ollama";
  }
  if (billingSource === "byo_subscription") {
    return "customer_paid_subscription";
  }
  if (billingSource === "byo_api") {
    return "customer_paid_byo";
  }
  if (billingSource === "platform_api") {
    return "platform_paid";
  }
  return "unknown";
}
